import * as React from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { WizardNav, type WizardStepDef } from './WizardNav';
import { WizardStepPanel } from './WizardStepPanel';

// Re-export the shared step descriptor so callers can import it from this module.
export type { WizardStepDef } from './WizardNav';

export interface WizardShellProps {
  // ---------- Chrome ----------
  /**
   * Optional title rendered in the wizard's own header bar (matches the
   * "Add Event" modal pattern: title on the left, X close button on the right,
   * dark sidebar-colored background, rounded top corners).
   * Omit to render without a header (legacy behavior).
   */
  title?: React.ReactNode;
  /**
   * Close handler. When supplied together with a `title`, renders an X icon
   * in the header's top-right corner. Also controls whether a Cancel button
   * is auto-rendered on the footer's right side.
   */
  onClose?: () => void;

  // ---------- Navigation ----------
  /** Ordered list of steps, rendered left-to-right (top-to-bottom in nav). */
  steps: WizardStepDef[];
  /** 1-based current step. Matches `WizardStep = 1 | 2 | 3 | 4` in the jackpot store. */
  currentStep: number;
  /**
   * Steps whose 1-based index <= completedUpTo are clickable in the nav.
   * Defaults to `currentStep - 1` so previously-visited steps are clickable
   * but future steps are gated behind validation.
   */
  completedUpTo?: number;
  /** Invoked with a 1-based step number when the user clicks a nav item. */
  onStepChange?: (step: number) => void;
  /**
   * 1-based step indices with missing required data. Forwarded to the nav so
   * the user can spot the step(s) that block Save Draft / Save & Publish.
   * Does NOT affect clickability — all steps are always clickable.
   */
  invalidSteps?: ReadonlySet<number> | number[];

  // ---------- Content ----------
  /** Content for the currently-active step (single-view mode). */
  children: React.ReactNode;
  /**
   * Optional per-step content rendered in expand-all mode.
   * Indexed 0-based (index 0 = step 1). If omitted, expand-all toggle is hidden.
   */
  allStepContent?: React.ReactNode[];

  // ---------- Footer ----------
  /** Handler for the default Next step-navigation button (bottom-left). */
  onNext?: () => void;
  /** Handler for the default Back step-navigation button (bottom-left). */
  onBack?: () => void;
  /** Disables the default Next step-navigation button. */
  isNextDisabled?: boolean;
  /** Disables the default Back step-navigation button. */
  isBackDisabled?: boolean;
  /**
   * Optional secondary action rendered on the footer's right side, before
   * the auto Cancel button. Typically a "Save Draft" outline button.
   */
  secondaryAction?: React.ReactNode;
  /**
   * Primary CTA rendered on the far right of the footer (e.g. "Save Event",
   * "Save & Publish"). Sits to the right of the auto Cancel button.
   */
  primaryAction?: React.ReactNode;
  /**
   * Override the Cancel button auto-rendered when `onClose` is supplied.
   * Pass `false` to hide it, or a node to replace it.
   */
  cancelButton?: React.ReactNode | false;
  /** Full override for the left footer slot (replaces Previous/Next pair). */
  footerLeft?: React.ReactNode;
  /** Full override for the right footer slot (replaces secondary/Cancel/primary). */
  footerRight?: React.ReactNode;

  // ---------- Pre-rendered content above footer (e.g. error text) ----------
  belowContent?: React.ReactNode;

  // ---------- Layout escape hatches ----------
  /** Extra className merged into the root wizard container. */
  className?: string;
}

/**
 * Two-column wizard chrome: optional header with title + close button, left
 * nav + right content pane, and a sticky footer with step navigation on the
 * left and action buttons on the right.
 *
 * Mirrors the visual pattern of the "Add Event" modal (EditEventDialog):
 *   - Header: title on left, X close button on right, dark sidebar-colored bg
 *   - Body: sidebar stepper + scrollable content
 *   - Footer bottom-left: Previous / Next step-navigation buttons
 *   - Footer bottom-right: [secondary] [Cancel] [primary]
 *   - Full rounded border with overflow hidden for clean corners
 *
 * Presentation-only: no knowledge of the host's state store, persistence,
 * toasts, query invalidation, or loading state. Caller owns all of that.
 * 1-based step indexing throughout (matches existing Zustand stores).
 */
export function WizardShell({
  title,
  onClose,
  steps,
  currentStep,
  completedUpTo,
  onStepChange,
  invalidSteps,
  children,
  allStepContent,
  onNext,
  onBack,
  isNextDisabled,
  isBackDisabled,
  secondaryAction,
  primaryAction,
  cancelButton,
  footerLeft,
  footerRight,
  belowContent,
  className,
}: WizardShellProps) {
  const [expandAll, setExpandAll] = React.useState(false);

  // If expand-all content isn't provided, force off and hide the toggle.
  const canExpandAll = Array.isArray(allStepContent) && allStepContent.length > 0;
  const effectiveExpandAll = canExpandAll && expandAll;

  // Default: steps strictly before the current one are clickable.
  const resolvedCompletedUpTo =
    typeof completedUpTo === 'number' ? completedUpTo : Math.max(0, currentStep - 1);

  const handleStepChange = React.useCallback(
    (step: number) => {
      onStepChange?.(step);
    },
    [onStepChange]
  );

  const handleExpandAllChange = React.useCallback(
    (v: boolean) => {
      if (!canExpandAll) return;
      setExpandAll(v);
    },
    [canExpandAll]
  );

  // Default left footer: both Previous and Next step-navigation buttons,
  // mirroring EditEventDialog's layout. Previous is disabled on step 1;
  // Next is disabled on the last step or when isNextDisabled is set.
  const isFirstStep = currentStep <= 1;
  const isLastStep = currentStep >= steps.length;
  const backDisabled = typeof isBackDisabled === 'boolean' ? isBackDisabled : isFirstStep;
  const nextDisabled = !!isNextDisabled || isLastStep || !onNext;

  const defaultLeft = effectiveExpandAll ? null : (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={onBack}
        disabled={backDisabled || !onBack}
      >
        Previous
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={onNext}
        disabled={nextDisabled}
      >
        Next
      </Button>
    </>
  );

  const leftSlot = footerLeft !== undefined ? footerLeft : defaultLeft;

  // Default right footer: [Cancel] [secondary] [primary]. Cancel is auto-
  // rendered when `onClose` is supplied, unless `cancelButton` overrides it
  // (pass `false` to hide, or a node to replace). Cancel sits leftmost so
  // the primary CTA stays on the far right.
  const cancelNode =
    cancelButton === false
      ? null
      : cancelButton !== undefined
        ? cancelButton
        : onClose
          ? (
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
            )
          : null;

  const defaultRight = (
    <>
      {cancelNode}
      {secondaryAction}
      {primaryAction}
    </>
  );

  const rightSlot = footerRight !== undefined ? footerRight : defaultRight;

  // When a title is provided, WizardShell owns the full modal chrome
  // (rounded border + overflow-hidden). Otherwise it renders bare (legacy)
  // so existing hosts wrapping in <Card> / <Dialog> aren't double-bordered.
  const hasChrome = title !== undefined;

  return (
    <div
      className={cn(
        'flex min-h-0 flex-1 flex-col',
        hasChrome && 'rounded-lg border bg-card overflow-hidden',
        className
      )}
    >
      {/* Fixed header with title + X close. Rendered only when `title` is set. */}
      {hasChrome && (
        <div
          className="border-b px-6 py-4 shrink-0"
          style={{
            background: 'hsl(var(--sidebar-background))',
            color: 'hsl(var(--sidebar-foreground))',
          }}
        >
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold">{title}</h2>
            {onClose && (
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="p-2 rounded-lg transition-colors opacity-70 hover:opacity-100"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Body: sidebar + content */}
      <div className="flex min-h-0 flex-1">
        <WizardNav
          steps={steps}
          currentStep={currentStep}
          completedUpTo={resolvedCompletedUpTo}
          onStepChange={handleStepChange}
          expandAll={effectiveExpandAll}
          onExpandAllChange={handleExpandAllChange}
          showExpandAllToggle={canExpandAll}
          invalidSteps={invalidSteps}
        />

        <WizardStepPanel>
          {effectiveExpandAll ? (
            <div className="space-y-8">
              {allStepContent!.map((content, idx) => (
                <div key={`step-${idx}`}>
                  {idx > 0 && <hr className="mb-8 border-border" />}
                  {content}
                </div>
              ))}
            </div>
          ) : (
            children
          )}
        </WizardStepPanel>
      </div>

      {/* Optional content wedged between panel and footer — typically error text. */}
      {belowContent ? (
        <div className="px-6 pt-3">
          {belowContent}
        </div>
      ) : null}

      {/* Fixed footer: Previous/Next on the left, actions on the right. */}
      <div className="border-t bg-background px-6 py-4 shrink-0">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">{leftSlot}</div>
          <div className="flex items-center gap-3">{rightSlot}</div>
        </div>
      </div>
    </div>
  );
}

export default WizardShell;
